import { Injectable } from '@angular/core';
import { Platform, AlertController } from '@ionic/angular';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { Router } from '@angular/router';
import { ApiService } from '../api/api.service';
import { AuthService } from '../auth/auth.service';
import { NotyfService } from '../notyf/notyf.service';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class FcmService {
  token: string = null;
  fcmIsSetup = false;
  private isSavingToken = false;
  private isLoggedIn = false;
  private topic: string = environment.fcm_topic;
  private lastNotificationId: string | null = null;

  constructor(
    private platform: Platform,
    private auth: AuthService,
    private api: ApiService,
    private notyf: NotyfService,
    private alertController: AlertController,
    private router: Router
  ) {
    this.auth.loggedIn$.subscribe((loggedIn) => {
      this.isLoggedIn = loggedIn;
      if (loggedIn && !this.fcmIsSetup) {
        this.setupFCM();
      } else if (!loggedIn && this.fcmIsSetup) {
        this.removeToken();
      }
    });
  }

  async setupFCM() {
    
    if (!this.platform.is('hybrid') || this.fcmIsSetup || !this.isLoggedIn) return;
  
    try {      
      const { receive } = await FirebaseMessaging.checkPermissions();
      console.log('Permission status:', receive);
  
      if (receive !== 'granted') {
        const { receive: requested } = await FirebaseMessaging.requestPermissions();
        console.log('Requested status:', requested);
  
        if (requested !== 'granted') {
          console.warn('FCM permission not granted');
          return;
        }
      }
  
      const { token } = await FirebaseMessaging.getToken();
      console.log('FCM Token: ', token);
      this.token = token;
      await this.saveToken(token);
  
      await FirebaseMessaging.addListener('tokenReceived', (event) => {
        console.log('New token received: ', event.token);
        this.token = event.token;
        this.saveToken(event.token);
      });
  
      await FirebaseMessaging.addListener('notificationReceived', async (message) => {
        const id = message.notification?.id;      
        console.log('Notification received in foreground: ================> ', JSON.stringify(message, null, 2));      
        if (id && id === this.lastNotificationId) {
          console.log('Duplicate notification ignored:', id);
          return; // Skip duplicate
        }      
        this.lastNotificationId = id;      
        if (message?.notification?.title && message?.notification?.body) {
          await this.showLocalNotification(message.notification);
        } else {
          console.warn('Invalid notification:', message);
        }
      });
  
      await FirebaseMessaging.addListener('notificationActionPerformed', (message) => {
        console.log('Notification tapped: ', message.notification);
        const data = message.notification?.data;
        const external_url = data?.['external_url'];
        const internal_url = data?.['internal_url'];
  
        if (external_url) {
          window.open(external_url, '_blank');
        } else if (internal_url) {
          this.router.navigateByUrl(`/${internal_url}`);
        }
      });
  
      this.fcmIsSetup = true;
  
      try {
        await FirebaseMessaging.subscribeToTopic({ topic: this.topic });
      } catch (e) {
        console.error('Failed to subscribe to topic:', e);
      }
    } catch (err) {
      console.error('Error setting up FCM:', err);
    }
  }
  

  async saveToken(token?: string) {
    if (!token || this.isSavingToken) return;
    this.isSavingToken = true;
  
    try {
      const response = await this.api.post('/user/save-device-token', { token }).toPromise() as { success?: boolean };
  
      if (!response?.success) {
        console.warn('Failed to save token');
      }
    } catch (error) {
      if (error.status === 401) {
        this.auth.logout();
      } else {
        console.error('Error saving device token:', error);
      }
    } finally {
      this.isSavingToken = false;
    }
  }  

  async showLocalNotification(notification: { title?: string; body?: string; data?: any }): Promise<void> {
    console.log("showLocalNotification => ", notification);
  
    const data = notification.data || {};
    const externalUrl = data.external_url;
    const internalUrl = data.internal_url;
  
    const buttons: any[] = [
      {
        text: 'OK',
        role: 'cancel',
      }
    ];
  
    if (externalUrl) {
      buttons.unshift({
        text: 'Open Link',
        handler: () => {
          window.open(externalUrl, '_blank');
        }
      });
    } else if (internalUrl) {
      buttons.unshift({
        text: 'Go To Page',
        handler: () => {
          // Navigate internally (make sure `Router` is injected)
          this.router.navigateByUrl('/' + internalUrl);
        }
      });
    }
  
    const alert = await this.alertController.create({
      header: notification.title || 'Notification',
      message: notification.body || 'No message',
      buttons: buttons,
    });
  
    await alert.present();
  }

  async removeToken() {
    if (!this.token) return;
    try {
      await this.api.post('/user/remove-device-token', { token: this.token }).toPromise();
    } catch (err) {
      console.error('Error removing device token:', err);
    } finally {
      this.token = null;
      this.fcmIsSetup = false;
    }
  }

  async checkAndSetupFCM() {
    if (!this.platform.is('hybrid') || this.fcmIsSetup || !this.isLoggedIn) return;
  
    try {
      const { receive } = await FirebaseMessaging.checkPermissions();
  
      if (receive !== 'granted') {
        const { receive: requested } = await FirebaseMessaging.requestPermissions();
        if (requested !== 'granted') {
          console.warn('FCM permission not granted');
          return;
        }
      }
  
      await this.setupFCM();
    } catch (error) {
      console.error('Error checking/requesting FCM permission:', error);
    }
  }
}
